import * as React from "react";
import * as Pushy from "@/api/Pushy";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App as CapApp } from "@capacitor/app";

import AppState from "@/realize-shared/model/AppState";
import type { RoomID } from "@/realize-shared/model/Types";

import * as AppRequests from "@/api/AppRequests";
import * as RoomRequests from "@/api/RoomRequests";

import type { Room, Rooms } from "@/game/types/Game";
import * as RoomManager from "@/game/events/RoomManager";
import { ReactGameContext } from "@/game/GameContext";
import {
    ReactAppContext,
    ReactFullAppContext,
    useAppContext,
    type FullAppContext
} from "@/game/AppContext";
import { GameUIState } from "@/game/GameUIState";

import DebugOverlay from "@/components/DebugOverlay";
import ErrorOverlay from "@/components/ErrorOverlay";
import DisableMask from "@/components/DisableMask";

import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Game from "@/pages/Game";
import PhotoSubmission from "@/pages/gameplay/PhotoSubmission";
import HeadlineCreation from "@/pages/gameplay/HeadlineCreation";

import { getReadableId } from "@/util/ReadableID";
import * as RealizeCache from "@/util/Cache";
import { Icons } from "./game/Icons";
import { MAX_NAME_LENGTH } from "./data/Config";
import type { UserIcon } from "./realize-shared/model/UserIcon";
import { splitMix32F } from "./realize-shared/Random";
import { cyrb53 } from "./util/Util";
import { useRoom } from "./game/events/useRoom";
import { Themes } from "./game/themes/Themes";

import Settings from "./game/Settings";
import AccountLocked from "./components/login-screen/AccountLocked";
import { PROD } from "./data/Environment";
import { Browser } from "@capacitor/browser";
import { isTokenValid, type UserData } from "./util/OAuth";
import UIListener from "./pages/home/UIListener";
import ErrorPopupContainer from "./components/errors/ErrorPopupContainer";
import NotificationContainer from "./components/notifications/NotificationContainer";

import { io, type Socket } from "socket.io-client";
import type { ClientToServerEvents, ServerToClientEvents } from "./local-shared/socket-events";
import { Notifications } from "./components/notifications/Notifications";
import { AnimatePresence } from "framer-motion";

const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(
    PROD ? "https://social.realizemag.com" : "http://localhost:3005",
    {
        reconnectionDelay: 10000,
        reconnectionDelayMax: 10000,
        autoConnect: true,
        transports: ["websocket", "polling"],
    }
);

// settings does stuff on import
void Settings;

const queryClient = new QueryClient();

const handleLogin = async (setUserState: React.Dispatch<React.SetStateAction<UserData | null>>) =>
{
    const tokenData = await isTokenValid();


    if (typeof tokenData === "boolean")
    {
        setUserState(null);

        return;
    }

    setUserState(tokenData.data.user);
};

const AppLoginHandler = ({
    setUserState,
    setError
}: {
    setUserState: React.Dispatch<React.SetStateAction<UserData | null>>,
    setError: React.Dispatch<React.SetStateAction<string | null>>
}) =>
{
    React.useEffect(() =>
    {
        // Handle the 'appUrlOpen' event and call `handleRedirectCallback`
        CapApp.addListener("appUrlOpen", ({ url }) =>
        {
            Logger.log("appUrlOpen", url);


            // Handle the URL and extract the token

            const isRealizeURL = url.startsWith("com.jumpbutton.realize://");

            if (!isRealizeURL)
            {
                Logger.error("Invalid URL: ", url);

                return;
            }

            // Extract the token from the URL

            const error = new URL(url).searchParams.get("authError");

            if (error)
            {
                Logger.error("Authentication error: ", error);

                Browser.close().catch((e) =>
                {
                    Logger.error("Error closing browser after auth error", e);
                });

                setError(error);

                return;
            }

            const token = new URL(url).searchParams.get("token");

            if (!token)
            {
                Logger.error("No token found in URL: ", url);

                Browser.close().catch((e) =>
                {
                    Logger.error("Error closing browser after no token", e);
                });

                return;
            }

            Logger.log(token);

            if (token)
            {
                // Store token in local storage
                localStorage.setItem("access_token", token);

                handleLogin(setUserState).catch((e) =>
                {
                    Logger.error("Error handling appUrlOpen", e);
                });
                // window.location.reload();
            }

            void (async () =>
            {
                Logger.log("appUrlOpen", url);

                await Browser.close();
            })();
        }).catch((e) =>
        {
            Logger.error("Error handling appUrlOpen", e);
        });
    }, [setUserState, setError]);

    return null;
};

export const AuthContext = React.createContext<UserData | null>(null);

const Realize = ({
    clientID = "",
    ready,
    error,
}: {
    clientID?: string;
    ready: () => void;
    error: (error: Error) => void;
}) =>
{
    // ----------
    // State stuffs
    // ----------
    const [gameState, setGameState] = React.useState<AppState>(AppState.DISCONNECTED);

    const [humanID, setHumanID] = React.useState("");

    const [username, setUsername] = React.useState(() =>
    {
        const stored = RealizeCache.get<string>("username");

        return stored ?? null;
    });

    const [locked, setLocked] = React.useState(false);

    const [avatar, setAvatar] = React.useState(() =>
    {
        const stored = RealizeCache.get<UserIcon>("avatar");

        if (stored) return Icons.get(stored.hash);

        return null;
    });

    const [theme, setTheme] = React.useState(() =>
    {
        const stored = RealizeCache.get<string>("theme");

        if (typeof stored === "string")
        {
            try
            {
                const actual = Themes.get(stored);

                if (!actual.locked) return actual;
            }
            catch (e)
            {
                Logger.warn(e);
            }
        }

        return Themes.default;
    } );

    const [spectatedRoom, setSpectatedRoom] = React.useState<RoomID | false>(false);

    /**
     * false: no request
     * true: pending request
     * string: active (or fulfilled) request
     */
    const [requestedRoom, setRequestedRoom] = React.useState<RoomID | boolean>(false);
    const [waiting, setWaiting] = React.useState(false);

    const [room, setRoom] = React.useState<Room | null>(null);
    const [rooms, setRooms] = React.useState<Rooms>(new Map());

    React.useMemo(() =>
    {
        if (username) RealizeCache.set("username", username);
    }, [username]);

    React.useMemo(() =>
    {
        if (avatar) RealizeCache.set("avatar", avatar);
    }, [avatar]);

    React.useMemo(() =>
    {
        RealizeCache.set("theme", theme.slug);
    }, [theme]);

    React.useMemo(() =>
    {
        if (!humanID) return;

        RoomManager.setHumanID(humanID);

        if (!username)
        {
            setUsername(getReadableId(humanID, MAX_NAME_LENGTH));
        }

        if (!avatar)
        {
            const owned = Icons.owned;
            const rand = splitMix32F(cyrb53(humanID))();
            const index = Math.floor(rand * owned.length);

            setAvatar(owned[index]);
        }
    }, [humanID, username, avatar]);

    React.useEffect(() =>
    {
        const isFocused = gameState === AppState.FOCUSED;
        const isFocusRequested = requestedRoom !== false;

        if (isFocused === isFocusRequested)
        {
            return void setWaiting(false);
        }

        if (isFocusRequested)
        {
            if (requestedRoom === true) return;

            const room = rooms.get(requestedRoom);

            if (!room) return;

            setGameState(AppState.FOCUSED);
            room.data.write({gameUIState: GameUIState.CHAT}, false);
            setRoom(room);
        }
        else
        {
            setGameState(AppState.HOME);
            setRoom(null);
        }

        // setWaiting(false);
    }, [gameState, requestedRoom, rooms]);

    React.useLayoutEffect(() => RoomManager.update(rooms, setRooms), [rooms]);

    // ----------
    // Function wrappers
    // ----------

    const appContext: FullAppContext = React.useMemo(() =>
    {
        const joinRoom: FullAppContext["joinRoom"] = async (data) =>
        {
            setWaiting(true);
            setRequestedRoom(true);

            try
            {
                if ("roomID" in data)
                {
                    await RoomRequests.sendJoinRoomRequest(data).catch((e) =>
                    {
                        const split = (e as Error).message;

                        setErrorState(split);

                        setWaiting(false);
                        setRequestedRoom(false);
                    });

                    setRequestedRoom(data.roomID);
                }
                else
                {
                    const roomID = (await RoomRequests.sendCreateRoomRequest(data)).roomID;

                    setRequestedRoom(roomID);
                }
            }
            catch (e)
            {
                setWaiting(false);
                setRequestedRoom(false);

                Logger.log("Join Error", e);

                throw e;
            }
            finally
            {
                // this finally keeps a weird lint error from showing up???
            }
        };

        const spectateRoom: FullAppContext["spectateRoom"] = (data) =>
        {
            Logger.log("Spectating room", data);
            setWaiting(true);
            setSpectatedRoom(data);
        };

        const leaveRoom = (roomID: RoomID) =>
        {
            const room = rooms.get(roomID);

            AssertIsset(room, "Room not found");

            if (room.data.alive)
            {
                setWaiting(true);
                setRequestedRoom(false);

                Logger.info("Leaving room...", roomID);

                void RoomRequests.sendLeaveRoomRequest(roomID);
            }
            else
            {
                rooms.delete(roomID);
                setRooms(new Map(rooms.entries()));
            }
        };

        const focusRoom = (roomID: RoomID | false) => void setRequestedRoom(roomID);

        return {
            humanID,

            locked,
            setLocked,

            room,
            setRoom,

            waiting,
            setWaiting,

            username,
            setUsername,

            avatar,
            setAvatar,

            theme,
            setTheme,

            rooms,
            setRooms,

            joinRoom,
            leaveRoom,
            focusRoom,
            spectateRoom,
            spectatedRoom: spectatedRoom,
        };
    }, [humanID, locked, room, waiting, username, avatar, theme, rooms, spectatedRoom]);

    // ----------
    // Components
    // ----------


    const App = React.useMemo(() =>
    {
        switch (gameState)
        {
            case AppState.DISCONNECTED:
                void (async () =>
                {
                    await Pushy.init();
                    setGameState(AppState.LOGIN);
                    ready();
                })().catch(error);

                return null;

            case AppState.LOGIN:
            {
                return (
                    <Login
                        deviceID={clientID}
                        onSetHumanID={async (hid: string) =>
                        {
                            const self = await AppRequests.login(hid);

                            setHumanID(self.currentClient);
                            setLocked(self.flaggedForDeletion !== 0);
                            setGameState(AppState.HOME);
                        }}
                    />
                );
            }

            case AppState.HOME:
            {
                return (

                    <ReactFullAppContext.Provider
                        value={appContext}
                    >
                        {locked
                            ? <AccountLocked />
                            : (
                                <QueryClientProvider client={queryClient}>
                                    <Home socket={socket} />
                                </QueryClientProvider>
                            )}
                    </ReactFullAppContext.Provider>
                );
            }

            case AppState.FOCUSED:
            {
                return (
                    <Focused exit={() => void setRequestedRoom(false)} />
                );
            }

            case AppState.STORE:
            {
                throw new Error("Not yet implemented");
            }
            // case GUIState.HISTORY:
            //     throw new Error("Not yet implemented");
        }
    }, [gameState, error, ready, clientID, appContext, locked]);


    const [userState, setUserState] = React.useState<UserData | null>(null);

    const [errorState, setErrorState] = React.useState<string|null>(null);

    React.useEffect(() =>
    {
        // This is for development tokens
        if (!PROD)
        {
        // Check for token in path
            const url = new URL(window.location.href);
            const token = url.searchParams.get("token");

            if (token)
            {
            // Remove token from URL
                url.searchParams.delete("token");
                window.history.replaceState({}, document.title, url.toString());

                // Store token in local storage
                localStorage.setItem("access_token", token);

                handleLogin(setUserState).catch((e) =>
                {
                    Logger.error("Error handling login from URL token", e);
                });
            }

            const error = new URL(url).searchParams.get("authError");

            if (error)
            {
                Logger.error("Authentication error: ", error);
                setErrorState(error);

                return;
            }
        }
    }, []);


    React.useEffect(() =>
    {
        (async () =>
        {
            const tokenData = await isTokenValid();


            if (typeof tokenData === "boolean")
            {
                setUserState(null);

                return;
            }

            setUserState(tokenData.data.user);
        })().catch((e) =>
        {
            Logger.error("Error in Realize", e);
        });

        const validateToken = () =>
        {
            (async () =>
            {
                const tokenData = await isTokenValid();

                if (typeof tokenData === "boolean")
                {
                    setUserState(null);

                    return;
                }

                Logger.log("Token validated", tokenData.data.user);

                setUserState(tokenData.data.user);
            })().catch((e) =>
            {
                Logger.error("Error validating token", e);
                setUserState(null);
                setErrorState("Token validation failed. Please log in again.");
            });
        };

        UIListener.listen("validateToken", validateToken);

        return () =>
        {
            UIListener.unlisten("validateToken", validateToken);
        };
    }, []);


    React.useEffect(() =>
    {
        const logout = () =>
        {
            localStorage.removeItem("access_token");
            setUserState(null);
        };

        UIListener.listen("logout", logout);

        return () =>
        {
            UIListener.unlisten("logout", logout);
        };
    });

    React.useEffect(() =>
    {
        socket.on("connect_error", (err) =>
        {
            Logger.error("Socket connection error:", err);
            // setErrorState("Socket connection error. Please try again later.");
        });

        socket.on("connect", () =>
        {
            Logger.log("Socket connected");

            // Send the identify event with the JWT token
            const token = localStorage.getItem("access_token");

            if (token)
            {
                Logger.log("Identifying socket with token:", token);
                socket.emit("identify", token);
            }
        });

        socket.on("spectatorList", (e) =>
        {
            Logger.log("Specators Change", e);


            UIListener.trigger("spectatorList", e);
        });

        socket.on("notification", (notification) =>
        {
            Logger.log("Notification received:", notification);

            Notifications.getInstance().addNotification(
                notification
            );
        });

        socket.on("disconnect", (reason) =>
        {
            Logger.warn("Socket disconnected:", reason);

            if (reason === "io server disconnect")
            {
                // the disconnection was initiated by the server, you need to reconnect manually
                socket.connect();
            }
        });
    }, []);

    React.useEffect(() =>
    {
        const token = localStorage.getItem("access_token");

        if (!token)
        {
            Logger.warn("No access token found in localStorage, cannot identify socket.");

            return;
        }

        Logger.log("Identifying socket with access token:", token);
        socket.emit("identify", token);
    }, [userState]);

    return (
        <React.StrictMode>
            <AuthContext.Provider value={userState}>
                <AppLoginHandler
                    setUserState={setUserState}
                    setError={setErrorState}
                />
                <ReactAppContext.Provider
                    value={appContext}
                >
                    <ErrorOverlay>
                        { App }
                        <AnimatePresence mode="wait">
                            { waiting && <DisableMask /> }
                        </AnimatePresence>
                        <DebugOverlay />
                    </ErrorOverlay>
                    <NotificationContainer />
                </ReactAppContext.Provider>
                {errorState && (
                    <ErrorPopupContainer
                        error={{ title: "Auth Error", error: errorState}}
                        setError={(e) =>
                        {
                            setErrorState(e as string | null);
                        }}
                    />
                )}
            </AuthContext.Provider>
        </React.StrictMode>
    );
};

const Focused = ({
    exit,
}: {
    exit: () => void;
}) =>
{
    const { room, humanID } = useAppContext();

    AssertIsset(room, "null room focused");
    AssertTrue(humanID !== "", "No humanID assigned");

    const gameData = useRoom(room);

    React.useEffect(() =>
    {
        if (gameData.gameUIState === GameUIState.EXIT)
        {
            exit();
        }
    }, [gameData.gameUIState, exit]);

    return (
        <ReactGameContext.Provider
            value={gameData}
        >
            {(gameData.gameUIState === GameUIState.CHAT) && (
                <Game
                    interactive
                    socket={socket}
                    exit={() => void room.data.write({gameUIState: GameUIState.EXIT}, true)}
                />
            )}
            {(gameData.gameUIState === GameUIState.CAMERA) && <PhotoSubmission />}
            {(gameData.gameUIState === GameUIState.HEADLINE) && <HeadlineCreation />}
        </ReactGameContext.Provider>
    );
};

export default Realize;
